import React, { useState, useEffect } from "react";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";
import { galleryItem, galleryInstanceProps } from "./types";
import { ImageUrl } from "../../../helpers/types";
import throttle from "lodash.throttle";
import useConstant from "use-constant";
import { useComponentSize } from "../../../helpers/hooks/useComponentSize";
import Lightbox from "../../../static/Lightbox/src/Lightbox";
import { mediaSizes } from "../../../helpers/utils/index";
import "./index.scss";
import GalleryThumbnails from "./GalleryThumbnails";
// @ts-ignore
import Img from "react-image";
import { lightboxEmitter } from "../../LatestMediaTabs/src";

interface galleryProps {
  gallery: galleryItem[];
  galleryLink?: string;
  showLB?: Boolean;
  setShowLB?: Function;
  isDesktopMode?: Boolean;
  style?: any;
  galleryLinkText?: string;
  imageGallerytitle?: string;
  isLightBox?: Boolean;
  selectedImage: number;
  setSelectedImage: Function;
  galleryWidth?: number;
}

export const getGalleryImg = (
  gallery: galleryItem[],
  selectedImage: number,
  isDesktopMode: Boolean
) => {
  let galleryImg;
  if (selectedImage >= 0 && selectedImage < gallery.length) {
    if (isDesktopMode) {
      galleryImg = gallery[selectedImage].imageUrls.filter((item: ImageUrl) => {
        return item.cropKey === "Large";
      })[0].imageUrl;
    } else {
      galleryImg = gallery[selectedImage].imageUrls.filter((item: ImageUrl) => {
        return item.cropKey === "Medium";
      })[0].imageUrl;
    }
  }
  return galleryImg;
};

export const GalleryInstance = (props: galleryProps) => {
  const [page, setPage] = useState(0);
  const [transitionType, setTransitionType] = useState<
    "fade" | "slide-right" | "slide-left"
  >("fade");
  let thumbnailsPerPage = 15;
  const {
    gallery,
    galleryLink,
    galleryLinkText,
    selectedImage,
    setSelectedImage
  } = props;

  // get widths of gallery for different viewport modes:
  let galleryRef = React.useRef(null);
  let gallerySize = useComponentSize(galleryRef);

  let isDesktopMode =
    props.isDesktopMode ||
    (gallerySize && gallerySize.width >= mediaSizes.tablet) ||
    (props.galleryWidth && props.galleryWidth >= mediaSizes.tablet);

  // set items per page mobile
  if (gallerySize && gallerySize.width < mediaSizes.smallMobile) {
    thumbnailsPerPage = 5;
  }

  // set items per page desktop
  if (
    gallerySize &&
    gallerySize.width >= mediaSizes.smallMobile &&
    !isDesktopMode
  ) {
    thumbnailsPerPage = 8;
  }

  let showNextThumbnailButton =
    Math.floor(gallery.length / thumbnailsPerPage) !== page;

  const changeThumbnailView = (index: number, page: number) => {
    setPage(page - index);
  };

  const changeSelectedImage = (index: number) => {
    setSelectedImage(index);
  };

  const onClickNextThumbnail = (page: number) => {
    setTransitionType("slide-left");
    changeThumbnailView(-1, page);
  };

  const onClickPrevThumbnail = (page: number) => {
    setTransitionType("slide-right");
    changeThumbnailView(1, page);
  };

  const throttledOnClickNextThumbnail = useConstant(() =>
    throttle(onClickNextThumbnail, 500)
  );

  const throttledOnClickPrevThumbnail = useConstant(() =>
    throttle(onClickPrevThumbnail, 500)
  );

  const handleIncrementUp = (
    selectedImage: number,
    thumbnailsPerPage: number,
    page: number
  ) => {
    setTransitionType("slide-left");

    // upper limit
    if (selectedImage + 1 < gallery.length) {
      if ((selectedImage + 1) % thumbnailsPerPage === 0) {
        changeThumbnailView(-1, page);
      }
      setSelectedImage(selectedImage + 1);
    }
  };

  const handleIncrementDown = (
    selectedImage: number,
    thumbnailsPerPage: number,
    page: number
  ) => {
    setTransitionType("slide-right");

    // lower limit
    if (selectedImage !== 0) {
      if (selectedImage % thumbnailsPerPage === 0) {
        changeThumbnailView(1, page);
      }
      setSelectedImage(selectedImage - 1);
    }
  };

  const throttledHandleIncrementDown = useConstant(() =>
    throttle(handleIncrementDown, 500)
  );

  const throttledHandleIncrementUp = useConstant(() =>
    throttle(handleIncrementUp, 500)
  );

  const galleryImg = getGalleryImg(gallery, selectedImage, isDesktopMode);

  // this is to preload the next image
  const nextGalleryImg = getGalleryImg(
    gallery,
    selectedImage + 1,
    isDesktopMode
  );
  const nextImg = new Image();
  if (nextGalleryImg) {
    nextImg.src = nextGalleryImg;
  }

  const prevGalleryImg = getGalleryImg(
    gallery,
    selectedImage - 1,
    isDesktopMode
  );
  const prevImg = new Image();
  if (prevGalleryImg) {
    prevImg.src = prevGalleryImg;
  }

  const resizeGalleryImage = ({ mount }: any) => {
    const galleryImage: HTMLElement | null = document.querySelector(
      ".gallery__carousel"
    );

    if (galleryImage !== null) {
      galleryImage.style.minHeight = `${(mount
        ? galleryImage.offsetWidth * 0.75
        : galleryImage.offsetWidth) * 0.667}px`;
    }
  };

  useEffect(() => {
    resizeGalleryImage({ mount: true });
    window.onresize = resizeGalleryImage;
    if (
      props.setShowLB &&
      gallery.length &&
      location.hash.toLocaleLowerCase().indexOf("gallerylightbox") > -1
    ) {
      props.setShowLB(true);
    }
    return window.removeEventListener("resize", resizeGalleryImage);
  }, []);

  return (
    <>
      <div
        style={...props.style}
        ref={galleryRef}
        className={"gallery " + (isDesktopMode ? "gallery--desktop-mode" : "")}
      >
        <div
          className="gallery__carousel"
          data-transition-type={transitionType}
        >
          <ReactCSSTransitionGroup
            transitionName="carousel"
            transitionEnterTimeout={300}
            transitionLeaveTimeout={300}
          >
            <span className="carousel__image" key={galleryImg}>
              <Img
                src={galleryImg}
                alt={gallery[selectedImage].description}
                onClick={() => {
                  props.setShowLB && props.setShowLB(true);
                  lightboxEmitter({ showLB: true });
                }}
                loader={<div className="gallery__image-loader" />}
                tabIndex={props.setShowLB ? 1 : undefined}
              />
            </span>
            <div className="gallery__credit">
              <span className="gallery__credit-value">
                {gallery[selectedImage].credit
                  ? `Credit: ${gallery[selectedImage].credit}`
                  : null}
              </span>
              <span className="gallery__title-value">
                {gallery[selectedImage].headline}
              </span>
              {gallery[selectedImage].circuitLogoUrl ? (
                <img
                  src={
                    isDesktopMode
                      ? gallery[selectedImage].circuitLogoLargeUrl
                      : gallery[selectedImage].circuitLogoUrl
                  }
                  alt={gallery[selectedImage].headline}
                />
              ) : null}
            </div>
          </ReactCSSTransitionGroup>
          {selectedImage !== 0 ? (
            <button
              onClick={() => {
                throttledHandleIncrementDown(
                  selectedImage,
                  thumbnailsPerPage,
                  page
                );
              }}
              className="btn--carousel btn--carousel-previous"
              tabIndex={0}
              aria-label="Previous slide"
            />
          ) : null}
          {selectedImage !== gallery.length - 1 ? (
            <button
              onClick={() =>
                throttledHandleIncrementUp(
                  selectedImage,
                  thumbnailsPerPage,
                  page
                )
              }
              className="btn--carousel btn--carousel-next"
              tabIndex={0}
              aria-label="Next slide"
            />
          ) : null}
        </div>
        <div
          className="gallery__selection-container"
          data-transition-type={transitionType}
        >
          <div
            className={
              "gallery__selection " +
              (!props.galleryLink ? "gallery__selection--no-link" : "")
            }
          >
            <ReactCSSTransitionGroup
              transitionName="thumbail-transition"
              transitionEnterTimeout={500}
              transitionLeaveTimeout={500}
            >
              <GalleryThumbnails
                key={page}
                gallery={gallery}
                page={page}
                thumbnailsPerPage={thumbnailsPerPage}
                selectedImage={selectedImage}
                setTransitionType={setTransitionType}
                changeSelectedImage={changeSelectedImage}
              />
            </ReactCSSTransitionGroup>
            <>
              {page !== 0 ? (
                <button
                  onClick={() => {
                    throttledOnClickPrevThumbnail(page);
                  }}
                  className="btn--carousel btn--carousel-previous"
                  tabIndex={0}
                  aria-label="Previous Gallery Thumbnails"
                />
              ) : null}
              {showNextThumbnailButton ? (
                <button
                  onClick={() => {
                    throttledOnClickNextThumbnail(page);
                  }}
                  className="btn--carousel btn--carousel-next"
                  tabIndex={0}
                  aria-label="Next Gallery Thumbnails"
                />
              ) : null}
            </>
            {galleryLink ? (
              <div className="see-all-container flex-container section">
                <a className="btn btn--primary" href={galleryLink}>
                  {galleryLinkText ? galleryLinkText : "See all photos"}
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

const Gallery = (props: galleryInstanceProps) => {
  const [showLB, setShowLB] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  return (
    <>
      {props.imageGallerytitle && props.imageGallerytitle.length > 0 ? (
        <div className="gallery__title">
          <h3>{props.imageGallerytitle}</h3>
        </div>
      ) : null}
      <GalleryInstance
        {...props}
        setShowLB={setShowLB}
        showLB={showLB}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
      />
      <Lightbox
        callback={() => {
          setShowLB(false);
          lightboxEmitter({ showLB: false });
        }}
        show={showLB}
        className={"lightbox--gallery"}
      >
        <GalleryInstance
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
          isLightBox
          {...props}
        />
      </Lightbox>
    </>
  );
};

export default Gallery;
